export enum GraphicsQuality
{
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export const GRAPHICS_QUALITY_ORDER: readonly GraphicsQuality[] = [
  GraphicsQuality.LOW,
  GraphicsQuality.MEDIUM,
  GraphicsQuality.HIGH,
];

export type ShadowMode = "off" | "basic" | "pcf" | "pcf_soft";

export interface GraphicsProfile
{
  msaa: boolean;
  shadows: ShadowMode;
  shadow_map_size: number;
  shadow_refresh_divisor: number;
  receive_shadows: boolean;
  rim_light: boolean;
  hemisphere_intensity: number;
  normal_maps: boolean;
  refraction: boolean;
  ball_shadow: boolean;
  sky_animation: boolean;
  specular: boolean;
  max_pixel_ratio: number;
  render_scale: number;
  ui_render_scale: number;
  ui_effects: boolean;
}

export function uiEffectsEnabled(): boolean
{
  return GraphicsSettings.profile().ui_effects;
}

const PROFILES: Record<GraphicsQuality, GraphicsProfile> = {
  [GraphicsQuality.HIGH]: {
    msaa: true,
    shadows: "pcf",
    shadow_map_size: 1024,
    shadow_refresh_divisor: 2,
    receive_shadows: true,
    rim_light: true,
    hemisphere_intensity: 0.3,
    normal_maps: true,
    refraction: true,
    ball_shadow: true,
    sky_animation: true,
    specular: true,
    max_pixel_ratio: 0,
    render_scale: 1,
    ui_render_scale: 1,
    ui_effects: true,
  },
  [GraphicsQuality.MEDIUM]: {
    msaa: true,
    shadows: "pcf",
    shadow_map_size: 1024,
    shadow_refresh_divisor: 2,
    receive_shadows: false,
    rim_light: false,
    hemisphere_intensity: 0.45,
    normal_maps: false,
    refraction: true,
    ball_shadow: true,
    sky_animation: true,
    specular: true,
    max_pixel_ratio: 0,
    render_scale: 1,
    ui_render_scale: 1,
    ui_effects: true,
  },
  [GraphicsQuality.LOW]: {
    msaa: false,
    shadows: "off",
    shadow_map_size: 1024,
    shadow_refresh_divisor: 2,
    receive_shadows: false,
    rim_light: false,
    hemisphere_intensity: 0.45,
    normal_maps: false,
    refraction: false,
    ball_shadow: false,
    sky_animation: false,
    specular: false,
    max_pixel_ratio: 2,
    render_scale: 1,
    ui_render_scale: 0.75,
    ui_effects: false,
  },
};

const STORAGE_KEY = "bb_graphics_quality_v1";
const OVERRIDE_KEY = "bb_graphics_profile_override_v1";
const DEFAULT_QUALITY = GraphicsQuality.HIGH;

export type GraphicsChangeSource = "user" | "override" | "remote";

export interface GraphicsRemoteState
{
  default_quality: GraphicsQuality | null;
  force_quality: GraphicsQuality | null;
  tiers: Partial<Record<GraphicsQuality, Partial<GraphicsProfile>>>;
}

type Listener = ( quality: GraphicsQuality, profile: GraphicsProfile, source: GraphicsChangeSource ) => void;

export function isGraphicsQuality( value: unknown ): value is GraphicsQuality
{
  return typeof value === "string" && GRAPHICS_QUALITY_ORDER.includes( value as GraphicsQuality );
}

function clampScale( scale: number ): number
{
  return Number.isFinite( scale ) && scale > 0 ? Math.min( 1, scale ) : 1;
}

export function effectivePixelRatio( profile: GraphicsProfile ): number
{
  const dpr = window.devicePixelRatio || 1;
  const capped = profile.max_pixel_ratio > 0 ? Math.min( dpr, profile.max_pixel_ratio ) : dpr;
  return capped * clampScale( profile.render_scale );
}

export function effectiveUiPixelRatio( profile: GraphicsProfile ): number
{
  const dpr = window.devicePixelRatio || 1;
  return dpr * clampScale( profile.ui_render_scale );
}

export class GraphicsSettings
{
  private static quality: GraphicsQuality | null = null;
  private static cached_profile: GraphicsProfile | null = null;
  private static listeners: Listener[] = [];
  private static remote: GraphicsRemoteState | null = null;

  public static get(): GraphicsQuality
  {
    if ( this.remote?.force_quality )
      return this.remote.force_quality;
    if ( this.quality === null )
      this.quality = this.readStored() ?? this.remote?.default_quality ?? DEFAULT_QUALITY;
    return this.quality;
  }

  public static hasStoredChoice(): boolean
  {
    return this.readStored() !== null;
  }

  public static set( quality: GraphicsQuality ): void
  {
    if ( !GRAPHICS_QUALITY_ORDER.includes( quality ) )
      return;

    const changed = quality !== this.get();
    this.quality = quality;
    this.cached_profile = null;
    try { localStorage.setItem( STORAGE_KEY, quality ); } catch {}

    if ( changed )
      this.notify( "user" );
  }

  public static setRemote( remote: GraphicsRemoteState | null ): void
  {
    const before_quality = this.get();
    const before_profile = JSON.stringify( this.profile() );

    this.remote = remote;
    this.cached_profile = null;
    if ( this.readStored() === null )
      this.quality = null;

    if ( before_quality !== this.get() || before_profile !== JSON.stringify( this.profile() ) )
      this.notify( "remote" );
  }

  public static getRemote(): GraphicsRemoteState | null
  {
    return this.remote;
  }

  public static profile(): GraphicsProfile
  {
    if ( this.cached_profile )
      return this.cached_profile;

    const quality = this.get();
    const base = { ...PROFILES[quality], ...( this.remote?.tiers[quality] ?? {} ) };
    const override = this.readOverride();
    this.cached_profile = override ? { ...base, ...override } : base;
    return this.cached_profile;
  }

  public static downgradesMaterials( profile: GraphicsProfile = this.profile() ): boolean
  {
    return !profile.specular || !profile.normal_maps || !profile.receive_shadows || profile.shadows === "off";
  }

  public static baseProfile( quality: GraphicsQuality ): GraphicsProfile
  {
    return { ...PROFILES[quality] };
  }

  public static setOverride( partial: Partial<GraphicsProfile> | null ): void
  {
    try
    {
      if ( partial && Object.keys( partial ).length > 0 )
        localStorage.setItem( OVERRIDE_KEY, JSON.stringify( partial ) );
      else
        localStorage.removeItem( OVERRIDE_KEY );
    }
    catch {}
    this.cached_profile = null;
    this.notify( "override" );
  }

  public static getOverride(): Partial<GraphicsProfile> | null
  {
    return this.readOverride();
  }

  public static subscribe( cb: Listener ): void
  {
    this.listeners.push( cb );
  }

  public static unsubscribe( cb: Listener ): void
  {
    this.listeners = this.listeners.filter( x => x !== cb );
  }

  public static describe(): { quality: GraphicsQuality; profile: GraphicsProfile; override: Partial<GraphicsProfile> | null; remote: GraphicsRemoteState | null; stored: GraphicsQuality | null }
  {
    return { quality: this.get(), profile: this.profile(), override: this.readOverride(), remote: this.remote, stored: this.readStored() };
  }

  private static notify( source: GraphicsChangeSource ): void
  {
    const quality = this.get();
    const profile = this.profile();
    for ( const cb of this.listeners.slice() )
    {
      try { cb( quality, profile, source ); }
      catch ( e ) { console.error( "[Graphics] listener failed", e ); }
    }
  }

  private static readStored(): GraphicsQuality | null
  {
    try
    {
      const raw = localStorage.getItem( STORAGE_KEY );
      if ( isGraphicsQuality( raw ) )
        return raw;
    }
    catch {}
    return null;
  }

  private static readOverride(): Partial<GraphicsProfile> | null
  {
    try
    {
      const raw = localStorage.getItem( OVERRIDE_KEY );
      if ( !raw ) return null;
      const parsed = JSON.parse( raw );
      return parsed && typeof parsed === "object" ? parsed as Partial<GraphicsProfile> : null;
    }
    catch
    {
      return null;
    }
  }
}
